import { supabase } from '@/lib/supabase';
import { Package, CreatePackageParams, UpdatePackageParams } from '@/features/packages/types';
import { changeTrackingService } from '@/services/changeTracking';

export const packageMutations = {
  createPackage: async (params: CreatePackageParams): Promise<Package> => {
    const { provider_id, version_id, region_id, plan_ids, category_ids, channel_ids, ...formData } = params;

    const { data: pkg, error } = await (supabase
      .from('packages' as any)
      .insert([
        {
          provider_id,
          version_id,
          region_id,
          ...formData,
          updated_at: new Date().toISOString()
        }
      ])
      .select()
      .single() as any);

    if (error) throw error;

    // Link plans if provided
    if (plan_ids && plan_ids.length > 0) {
      await (supabase.from('package_plans' as any).insert(
        plan_ids.map((planId: number, idx: number) => ({
          package_id: pkg.id,
          plan_id: planId,
          version_id,
          display_order: idx
        }))
      ) as any);
    }

    // Link categories if provided
    if (category_ids && category_ids.length > 0) {
      await (supabase.from('package_categories' as any).insert(
        category_ids.map((categoryId: number, idx: number) => ({
          package_id: pkg.id,
          category_id: categoryId,
          version_id,
          display_order: idx
        }))
      ) as any);
    }

    // Link channels if provided
    if (channel_ids && channel_ids.length > 0) {
      await (supabase.from('package_channels' as any).insert(
        channel_ids.map((channelId: number, idx: number) => ({
          package_id: pkg.id,
          channel_id: channelId,
          version_id,
          display_order: idx
        }))
      ) as any);
    }

    // Track package creation
    try {
      await changeTrackingService.logEntityCreation({
        versionId: version_id,
        providerId: provider_id,
        regionId: region_id,
        entityType: 'package',
        entityId: (pkg as any).id,
        entityData: {
          id: (pkg as any).id,
          name: (pkg as any).name,
          description: (pkg as any).description,
          package_type: (pkg as any).package_type
        }
      });
    } catch (trackingError) {
      console.error('Failed to track package creation:', trackingError);
    }

    return pkg as any;
  },

  updatePackage: async (params: UpdatePackageParams): Promise<Package> => {
    const { id, data, plan_ids, category_ids, channel_ids } = params;

    // Get old package data for change tracking
    const { data: oldPackage } = await (supabase
      .from('packages' as any)
      .select('*')
      .eq('id', id)
      .single() as any);

    if (!oldPackage) throw new Error('Package not found');

    const { data: pkg, error } = await (supabase
      .from('packages' as any)
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single() as any);

    if (error) throw error;

    // Update plans if provided
    if (plan_ids !== undefined) {
      await (supabase.from('package_plans' as any).delete().eq('package_id', id) as any);
      if (plan_ids.length > 0) {
        await (supabase.from('package_plans' as any).insert(
          plan_ids.map((planId: number, idx: number) => ({
            package_id: id,
            plan_id: planId,
            version_id: (pkg as any).version_id,
            display_order: idx
          }))
        ) as any);
      }
    }

    // Update categories if provided
    if (category_ids !== undefined) {
      await (supabase.from('package_categories' as any).delete().eq('package_id', id) as any);
      if (category_ids.length > 0) {
        await (supabase.from('package_categories' as any).insert(
          category_ids.map((categoryId: number, idx: number) => ({
            package_id: id,
            category_id: categoryId,
            version_id: (pkg as any).version_id,
            display_order: idx
          }))
        ) as any);
      }
    }

    // Update channels if provided
    if (channel_ids !== undefined) {
      await (supabase.from('package_channels' as any).delete().eq('package_id', id) as any);
      if (channel_ids.length > 0) {
        await (supabase.from('package_channels' as any).insert(
          channel_ids.map((channelId: number, idx: number) => ({
            package_id: id,
            channel_id: channelId,
            version_id: (pkg as any).version_id,
            display_order: idx
          }))
        ) as any);
      }
    }

    // Track package update
    try {
      const changedFields = Object.keys(data).filter(
        key => (data as any)[key] !== (oldPackage as any)[key]
      );

      for (const field of changedFields) {
        await changeTrackingService.logChange({
          versionId: (pkg as any).version_id,
          providerId: (pkg as any).provider_id,
          regionId: (pkg as any).region_id,
          entityType: 'packages',
          entityId: (pkg as any).id,
          fieldName: field,
          actionType: 'UPDATE',
          oldValue: String((oldPackage as any)[field]),
          newValue: String((pkg as any)[field])
        });
      }
    } catch (trackingError) {
      console.error('Failed to track package update:', trackingError);
    }

    return pkg as any;
  },

  deletePackage: async (packageId: number): Promise<void> => {
    // Get package data before deletion
    const { data: pkg } = await (supabase
      .from('packages' as any)
      .select('*')
      .eq('id', packageId)
      .single() as any);

    if (!pkg) throw new Error('Package not found');

    // Delete all relationships first (cascade handled by DB)
    await (supabase.from('package_plans' as any).delete().eq('package_id', packageId) as any);
    await (supabase.from('package_categories' as any).delete().eq('package_id', packageId) as any);
    await (supabase.from('package_channels' as any).delete().eq('package_id', packageId) as any);

    // Delete package
    const { error } = await (supabase
      .from('packages' as any)
      .delete()
      .eq('id', packageId) as any);

    if (error) throw error;

    // Track package deletion
    try {
      await changeTrackingService.logEntityDeletion({
        versionId: (pkg as any).version_id,
        providerId: (pkg as any).provider_id,
        regionId: (pkg as any).region_id,
        entityType: 'package',
        entityId: (pkg as any).id,
        entityData: {
          id: (pkg as any).id,
          name: (pkg as any).name,
          description: (pkg as any).description,
          package_type: (pkg as any).package_type
        }
      });
    } catch (trackingError) {
      console.error('Failed to track package deletion:', trackingError);
    }
  },

  // Relationship mutations
  addPlanToPackage: async (packageId: number, planId: number, versionId: number): Promise<void> => {
    // Get max display order for this package
    const { data: existing } = await (supabase
      .from('package_plans' as any)
      .select('display_order')
      .eq('package_id', packageId)
      .order('display_order', { ascending: false })
      .limit(1) as any);

    const displayOrder = ((existing as any)?.[0]?.display_order ?? -1) + 1;

    const { error } = await (supabase
      .from('package_plans' as any)
      .insert([{ package_id: packageId, plan_id: planId, version_id: versionId, display_order: displayOrder }]) as any);

    if (error) throw error;
  },

  removePlanFromPackage: async (packageId: number, planId: number): Promise<void> => {
    const { error } = await (supabase
      .from('package_plans' as any)
      .delete()
      .eq('package_id', packageId)
      .eq('plan_id', planId) as any);

    if (error) throw error;
  },

  addCategoryToPackage: async (packageId: number, categoryId: number, versionId: number): Promise<void> => {
    // Get max display order for this package
    const { data: existing } = await (supabase
      .from('package_categories' as any)
      .select('display_order')
      .eq('package_id', packageId)
      .order('display_order', { ascending: false })
      .limit(1) as any);

    const displayOrder = ((existing as any)?.[0]?.display_order ?? -1) + 1;

    const { error } = await (supabase
      .from('package_categories' as any)
      .insert([{ package_id: packageId, category_id: categoryId, version_id: versionId, display_order: displayOrder }]) as any);

    if (error) throw error;
  },

  removeCategoryFromPackage: async (packageId: number, categoryId: number): Promise<void> => {
    const { error } = await (supabase
      .from('package_categories' as any)
      .delete()
      .eq('package_id', packageId)
      .eq('category_id', categoryId) as any);

    if (error) throw error;
  },

  addChannelToPackage: async (packageId: number, channelId: number, versionId: number): Promise<void> => {
    // Get max display order for this package
    const { data: existing } = await (supabase
      .from('package_channels' as any)
      .select('display_order')
      .eq('package_id', packageId)
      .order('display_order', { ascending: false })
      .limit(1) as any);

    const displayOrder = ((existing as any)?.[0]?.display_order ?? -1) + 1;

    const { error } = await (supabase
      .from('package_channels' as any)
      .insert([{ package_id: packageId, channel_id: channelId, version_id: versionId, display_order: displayOrder }]) as any);

    if (error) throw error;
  },

  removeChannelFromPackage: async (packageId: number, channelId: number): Promise<void> => {
    const { error } = await (supabase
      .from('package_channels' as any)
      .delete()
      .eq('package_id', packageId)
      .eq('channel_id', channelId) as any);

    if (error) throw error;
  }
};
